import { BUSINESS_PLAN_TYPES, IBusinessToolType } from 'constants/businessPlan';
import { t } from 'i18next';
import { icons } from 'PDF/assets/icons';
import { IChartWithBars } from 'PDF/components/Charts/types';
import { IStripedTableRow } from 'PDF/components/StripedTable';
import { IInfoObject } from 'PDF/components/StripedTable/StripedTable';
import { getColorStylesByRecord, getColorType } from 'PDF/helpers/documentStyle';
import { formatCurrency } from 'PDF/helpers/formatMoneyPDF';
import { getMagazineTextObject, IInfoTableRow } from 'PDF/helpers/pdfMapping';
import { BusinessOperationsKeys } from 'PDF/types/pdfSections';

import { IEquipmentSection } from 'components/BusinessToolForm/Sections/BusinessOperations/Equipment/utils/form';
import {
  IPremisesInformation,
  PREMISES_INFORMATION_NAMES,
} from 'components/BusinessToolForm/Sections/BusinessOperations/LocationAndPremises/utils/form';
import { IGlobalProductEntity } from 'components/BusinessToolForm/Sections/Global/general/products';
import { IBusinessToolForm } from 'components/BusinessToolForm/utils/form';

import { getCurrencySymbol } from 'helpers/getCurrencySymbol';

import { colorIndexesProductPlans } from './chartColors';
import { getDimensionalityForExamplePage } from '../MarketAnalysis/helpers';
import { getEnglishUnit } from '../MarketingAndSales/helpers';

interface IProductGroups {
  [key: string]: {
    products: IGlobalProductEntity[];
    data: number[][];
  };
}

const MAX_ITEMS_PER_CHART = 3;

export const getMagazineItems = (
  businessPlanChapters: IBusinessToolForm,
  businessPlanType: IBusinessToolType,
  isExamplePage: boolean,
) => {
  const {
    locationAndPremises: { location, qualities: locationAndPremisesQualities, futurePlans },
    equipment: {
      description: equipmentDescription,
      levelOfUtilization,
      rationaleForPurchasing: equipmentRationaleForPurchasing,
    },
    otherAssets: { description: otherAssetsDescription, rationaleForPurchasing: otherRationaleForPurchasing },
    technologicalProcess: { technology, qualities: technologicalProcessQualities, competingTechnology },
    workingResources: { availability, necessaryRecourses },
    partnersSection: { criteria },
    quality: { methods, factors },
    production: {
      specifics,
      inventoryManagement,
      package: productionPackage,
      outsourcingOpportunities,
      involvedProduction,
      disposalWaist,
    },
  } = businessPlanChapters.BUSINESS_OPERATIONS;
  const { competitionAdvantage } = businessPlanChapters.GLOBAL;
  const {
    description: { qualityStandards, productsOrServicesAndTheirPurpose },
    advantages: { productValue },
  } = businessPlanChapters.PRODUCTS_AND_SERVICES;

  if (businessPlanType === BUSINESS_PLAN_TYPES.FOR_INVESTORS) {
    return [
      [
        getMagazineTextObject(
          isExamplePage ? 'Premises Location. ' : t('pdfPages.mappings.businessOperations.premisesLocation'),
          location,
        ),
        getMagazineTextObject(
          isExamplePage ? 'Future Expansion. ' : t('pdfPages.mappings.businessOperations.futureExpansion'),
          futurePlans,
        ),
      ],
      [
        getMagazineTextObject(
          isExamplePage ? 'Equipment Usage. ' : t('pdfPages.mappings.businessOperations.equipmentUsage'),
          equipmentDescription,
        ),
      ],
      [
        getMagazineTextObject(
          isExamplePage ? 'Non-Current Assets. ' : t('pdfPages.mappings.businessOperations.nonCurrentAssets'),
          otherAssetsDescription,
        ),
      ],
      [
        getMagazineTextObject(
          isExamplePage ? 'Production Technology. ' : t('pdfPages.mappings.businessOperations.productionTechnology'),
          technology,
        ),
      ],
      [
        getMagazineTextObject(
          isExamplePage ? 'Material Requirements. ' : t('pdfPages.mappings.businessOperations.materialRequirements'),
          necessaryRecourses,
        ),
      ],
      [
        getMagazineTextObject(
          isExamplePage ? 'Production Specifics. ' : t('pdfPages.mappings.businessOperations.productionSpecifics'),
          specifics,
        ),
      ],
      [
        getMagazineTextObject(
          isExamplePage
            ? 'Products/Services Overview. '
            : t('pdfPages.mappings.businessOperations.productsServicesOverview'),
          productsOrServicesAndTheirPurpose,
        ),
      ],
      [
        getMagazineTextObject(
          isExamplePage ? 'Quality Standards. ' : t('pdfPages.mappings.businessOperations.qualityStandards'),
          qualityStandards,
        ),
      ],
      [
        getMagazineTextObject(
          isExamplePage ? 'Product Uniqueness. ' : t('pdfPages.mappings.businessOperations.productUniqueness'),
          competitionAdvantage,
        ),
        getMagazineTextObject(
          isExamplePage ? 'Customer Value. ' : t('pdfPages.mappings.businessOperations.customerValue'),
          productValue,
        ),
      ],
    ];
  }

  return [
    [
      getMagazineTextObject(
        isExamplePage ? 'Premises Location. ' : t('pdfPages.mappings.businessOperations.premisesLocation'),
        location,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage ? 'Location Pros/Cons. ' : t('pdfPages.mappings.businessOperations.locationProsCons'),
        locationAndPremisesQualities,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Future Expansion. ' : t('pdfPages.mappings.businessOperations.futureExpansion'),
        futurePlans,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage ? 'Equipment Usage. ' : t('pdfPages.mappings.businessOperations.equipmentUsage'),
        equipmentDescription,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage ? 'Utilization Levels. ' : t('pdfPages.mappings.businessOperations.utilizationLevels'),
        levelOfUtilization,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage ? 'Purchase Rationale. ' : t('pdfPages.mappings.businessOperations.purchaseRationale'),
        equipmentRationaleForPurchasing,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage ? 'Non-Current Assets. ' : t('pdfPages.mappings.businessOperations.nonCurrentAssets'),
        otherAssetsDescription,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage
          ? 'Acquisition Justification. '
          : t('pdfPages.mappings.businessOperations.acquisitionJustification'),
        otherRationaleForPurchasing,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage ? 'Production Technology. ' : t('pdfPages.mappings.businessOperations.productionTechnology'),
        technology,
      ),
      getMagazineTextObject(
        isExamplePage
          ? 'Advantages/Disadvantages. '
          : t('pdfPages.mappings.businessOperations.advantagesDisadvantages'),
        technologicalProcessQualities,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage ? 'Competing Technologies. ' : t('pdfPages.mappings.businessOperations.competingTechnologies'),
        competingTechnology,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage ? 'Material Requirements. ' : t('pdfPages.mappings.businessOperations.materialRequirements'),
        necessaryRecourses,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Resource Stability. ' : t('pdfPages.mappings.businessOperations.resourceStability'),
        availability,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage ? 'Selection Criteria. ' : t('pdfPages.mappings.businessOperations.selectionCriteria'),
        criteria,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage ? 'Production Specifics. ' : t('pdfPages.mappings.businessOperations.productionSpecifics'),
        specifics,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Packaging. ' : t('pdfPages.mappings.businessOperations.packaging'),
        productionPackage,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Inventory Management. ' : t('pdfPages.mappings.businessOperations.inventoryManagement'),
        inventoryManagement,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Outsourcing Options. ' : t('pdfPages.mappings.businessOperations.outsourcingOptions'),
        outsourcingOpportunities,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Third-Party Involvement. ' : t('pdfPages.mappings.businessOperations.thirdPartyInvolvement'),
        involvedProduction,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Waste Management. ' : t('pdfPages.mappings.businessOperations.wasteManagement'),
        disposalWaist,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage ? 'Quality Factors. ' : t('pdfPages.mappings.businessOperations.qualityFactors'),
        factors,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Control Methods. ' : t('pdfPages.mappings.businessOperations.controlMethods'),
        methods,
      ),
    ],
  ];
};

export const getBuildingsPremisesDetails = (businessPlanChapters: IBusinessToolForm, isExamplePage: boolean) => {
  const { premisesInformation } = businessPlanChapters.BUSINESS_OPERATIONS.locationAndPremises;
  const { currency } = businessPlanChapters.GLOBAL;
  const currencySymbol = getCurrencySymbol(currency);
  const { AddressTableIcon, AdressSixIcon, AreaIcon, PurposeIcon, TermsOfUseIcon, CurrentCostIcon } =
    icons[getColorType()];

  return premisesInformation.reduce<IStripedTableRow[]>(
    (result, item) => {
      result.forEach((row) => {
        switch (row.id) {
          case 'termsOfUse': {
            // TODO wrong type in the IPremisesInformation for termsOfUse. Should be fixed
            row.info.push(
              // @ts-expect-error no types
              item.termsOfUse?.value
                ? isExamplePage
                  ? // @ts-expect-error no types
                    item.termsOfUse?.value
                  : t(
                      // @ts-expect-error no types
                      `pdfPages.mappings.businessOperations.premisesTermsOfUseOptions.${item.termsOfUse?.value}`,
                    )
                : '',
            );
            break;
          }
          case 'area': {
            row.info.push(
              item.area && item.area !== '0'
                ? { value: `${item.area} ${isExamplePage ? 'm²' : t('justUnits.square_meter')}` }
                : { value: '' },
            );
            break;
          }
          case 'file': {
            row.info.push(item.file[0]?.url || '');
            row.imgValueSettings = { height: '80px' };
            break;
          }
          case 'currentCost': {
            row.info.push(
              item[PREMISES_INFORMATION_NAMES.CURRENT_COST] && item[PREMISES_INFORMATION_NAMES.CURRENT_COST] !== '0'
                ? formatCurrency(+item[PREMISES_INFORMATION_NAMES.CURRENT_COST], currencySymbol)
                : '',
            );

            break;
          }
          default:
            row.info.push(item[row.id as keyof Omit<IPremisesInformation, 'file' | 'termsOfUse'>]);
        }
      });
      return result;
    },
    [
      {
        id: 'address',
        imgSrc: AddressTableIcon,
        heading: isExamplePage ? 'Address' : t('pdfPages.mappings.businessOperations.address'),
        info: [],
      },
      {
        id: 'file',
        imgSrc: AdressSixIcon,
        heading: isExamplePage ? 'Photo' : t('pdfPages.mappings.businessOperations.photo'),
        info: [],
      },
      {
        id: 'area',
        imgSrc: AreaIcon,
        heading: isExamplePage ? 'Area' : t('pdfPages.mappings.businessOperations.area'),
        info: [],
      },
      {
        id: 'currentCost',
        imgSrc: CurrentCostIcon,
        heading: isExamplePage ? 'Current Cost' : t('pdfPages.mappings.businessOperations.currentCost'),
        info: [],
      },
      {
        id: 'purpose',
        imgSrc: PurposeIcon,
        heading: isExamplePage ? 'Purpose' : t('pdfPages.mappings.businessOperations.purpose'),
        info: [],
      },
      {
        id: 'termsOfUse',
        imgSrc: TermsOfUseIcon,
        heading: isExamplePage ? 'Terms of Use' : t('pdfPages.mappings.businessOperations.termUse'),
        info: [],
      },
    ],
  );
};

export const getEquipment = (
  equipment: IEquipmentSection[],
  costTitle: string,
  currencySymbol: string,
  isExamplePage: boolean,
): IInfoTableRow[][] => {
  const { AddressTableIcon, CurrentCostIcon, NameOfGroupIcon, NumberOfItemsIcon, PurposeIcon } = icons[getColorType()];
  return equipment
    .map(({ name, purpose, number, cost, file }) => {
      const imgSrc = file[0]?.url;

      return [
        {
          id: 'name',
          info: name,
          title: isExamplePage ? 'Name of the Group' : t('pdfPages.mappings.businessOperations.nameOfGroup'),
          iconSrc: NameOfGroupIcon,
        },
        {
          id: 'purpose',
          info: purpose,
          title: isExamplePage ? 'Purpose' : t('pdfPages.mappings.businessOperations.purpose'),
          iconSrc: PurposeIcon,
        },
        {
          id: 'number',
          info: number && number !== '0' ? number : '',
          title: isExamplePage
            ? 'Number of Items in the Group'
            : t('pdfPages.mappings.businessOperations.nameOfItemsInGroup'),
          iconSrc: NumberOfItemsIcon,
        },
        {
          id: 'cost',
          info: cost && cost !== '0' ? formatCurrency(+cost, currencySymbol) : '',
          title: costTitle,
          iconSrc: CurrentCostIcon,
        },
        {
          id: 'file',
          info: imgSrc ? { imgSrc } : '',
          title: isExamplePage ? 'Photo' : t('pdfPages.mappings.businessOperations.photo'),
          iconSrc: AddressTableIcon,
        },
      ].filter(({ info }) => info);
    })
    .filter((equipment) => equipment.length);
};

export const getEquipments = (businessPlanChapters: IBusinessToolForm, isExamplePage: boolean) => {
  const {
    equipment: { mainEquipment, purchasedEquipment },
    otherAssets: { purchasedEquipment: futureAcquisitions, mainEquipment: assetGroups },
  } = businessPlanChapters.BUSINESS_OPERATIONS;
  const { currency } = businessPlanChapters.GLOBAL;
  const currencySymbol = getCurrencySymbol(currency);

  return {
    equipmentGroups: getEquipment(
      mainEquipment,
      isExamplePage ? 'Current Cost' : t('pdfPages.mappings.businessOperations.currentCost'),
      currencySymbol,
      isExamplePage,
    ),
    plannedPurchases: getEquipment(
      purchasedEquipment,
      isExamplePage ? 'Planned Cost' : t('pdfPages.mappings.businessOperations.plannedCost'),
      currencySymbol,
      isExamplePage,
    ),
    assetGroups: getEquipment(
      assetGroups,
      isExamplePage ? 'Current Cost' : t('pdfPages.mappings.businessOperations.currentCost'),
      currencySymbol,
      isExamplePage,
    ),
    futureAcquisitions: getEquipment(
      futureAcquisitions,
      isExamplePage ? 'Planned Cost' : t('pdfPages.mappings.businessOperations.plannedCost'),
      currencySymbol,
      isExamplePage,
    ),
  };
};

const STEP_SYMBOLS = ['A', 'B', 'C', 'D', 'E'];
export const getProcessStages = (businessPlanChapters: IBusinessToolForm) => {
  const {
    technologicalProcess: { stages },
  } = businessPlanChapters.BUSINESS_OPERATIONS;
  const processStages = stages
    .filter(({ stage }) => stage)
    .map(({ stage }, index) => ({ title: stage, step: STEP_SYMBOLS[index] }));

  return {
    processStages,
    isVisible: !!processStages.filter(({ title }) => title).length,
  };
};

export const getSupplierOverviewData = (businessPlanChapters: IBusinessToolForm, isExamplePage: boolean) => {
  const {
    partnersSection: { keySuppliers },
  } = businessPlanChapters.BUSINESS_OPERATIONS;
  const { DeliveryTermsIcon, PriceIcon, QuantityPerMonthIcon, ServiceProductIcon, SupplierNameIcon } =
    icons[getColorType()];

  const { currency } = businessPlanChapters.GLOBAL;
  const currencySymbol = getCurrencySymbol(currency);

  return keySuppliers
    .map(({ name, product, quantity, price, deliveryTerms }) =>
      [
        {
          id: 'name',
          info: name,
          title: isExamplePage ? 'Supplier Name' : t('pdfPages.mappings.businessOperations.supplierName'),
          iconSrc: SupplierNameIcon,
        },
        {
          id: 'product',
          info: product,
          title: isExamplePage ? 'Service/Product' : t('pdfPages.mappings.businessOperations.serviceProduct'),
          iconSrc: ServiceProductIcon,
        },
        {
          id: 'quantity',
          info: quantity,
          title: isExamplePage ? 'Quantity per Month' : t('pdfPages.mappings.businessOperations.quantityPerMonth'),
          iconSrc: QuantityPerMonthIcon,
        },
        {
          id: 'Price',
          info: price && price !== '0' ? formatCurrency(+price, currencySymbol) : '',
          title: isExamplePage ? 'Price' : t('pdfPages.mappings.businessOperations.price'),
          iconSrc: PriceIcon,
        },
        {
          id: 'deliveryTerms',
          info: deliveryTerms
            ? isExamplePage
              ? typeof deliveryTerms === 'string'
                ? deliveryTerms
                : deliveryTerms?.value
              : t(
                  `pdfPages.mappings.businessOperations.deliveryTermsOptions.${typeof deliveryTerms === 'string' ? deliveryTerms : deliveryTerms?.value}`,
                )
            : '',
          title: isExamplePage ? 'Delivery Terms' : t('pdfPages.mappings.businessOperations.deliveryTerms'),
          iconSrc: DeliveryTermsIcon,
        },
      ].filter(({ info }) => info),
    )
    .filter((keySupplier) => keySupplier.length > 1);
};

export const getProductionPlans = (
  businessPlanChapters: IBusinessToolForm,
  isExamplePage: boolean,
): IChartWithBars[] => {
  const {
    production: { plannedProductsVolume },
  } = businessPlanChapters.BUSINESS_OPERATIONS;
  const { products, period, periodTableHeadings, unit } = businessPlanChapters.GLOBAL;

  if (!products || !plannedProductsVolume) {
    return [];
  }

  const groups = products.reduce<IProductGroups>((res, product, index) => {
    if (!product.volumeUnits) {
      return res;
    }

    if (!res[product.volumeUnits]) {
      res[product.volumeUnits] = { products: [], data: [] };
    }

    const group = res[product.volumeUnits];

    group.products.push(product);
    group.data.push([...Object.values(plannedProductsVolume[index]?.[period]).map(Number)]);

    return res;
  }, {});

  const colors = getColorStylesByRecord(colorIndexesProductPlans);

  const groupsToSplit = Object.entries(groups)
    .filter(([, { data }]) => data.length > MAX_ITEMS_PER_CHART)
    .reduce(
      (acc: IChartWithBars[], [volumeUnits, { products: groupedProducts, data }]) => [
        ...acc,
        {
          title: '',
          colors,
          source: '',
          unit: volumeUnits
            ? `${unit === 'single' ? '' : isExamplePage ? getDimensionalityForExamplePage(unit) + ' ' : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`) + ' '}${isExamplePage ? getEnglishUnit(volumeUnits) : t(`justUnits.${volumeUnits}`)}`
            : '',
          data: data.slice(0, MAX_ITEMS_PER_CHART),
          labels: Object.values(periodTableHeadings[period]),
          datasetsLabels: groupedProducts.slice(0, 3).map(({ name }) => name || ''),
        },
        {
          title: '',
          colors: colors.slice(MAX_ITEMS_PER_CHART),
          source: '',
          unit: volumeUnits
            ? `${unit === 'single' ? '' : isExamplePage ? getDimensionalityForExamplePage(unit) + ' ' : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`) + ' '}${isExamplePage ? getEnglishUnit(volumeUnits) : t(`justUnits.${volumeUnits}`)}`
            : '',
          data: data.slice(MAX_ITEMS_PER_CHART),
          labels: Object.values(periodTableHeadings[period]),
          datasetsLabels: groupedProducts.slice(3).map(({ name }) => name || ''),
        },
      ],
      [],
    );

  const restGroups = Object.entries(groups)
    .filter(([, { data }]) => data.length <= 3)
    .map(([volumeUnits, { products: groupedProducts, data }]) => ({
      title: '',
      colors,
      source: '',
      unit: volumeUnits
        ? `${unit === 'single' ? '' : isExamplePage ? getDimensionalityForExamplePage(unit) + ' ' : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`) + ' '}${isExamplePage ? getEnglishUnit(volumeUnits) : t(`justUnits.${volumeUnits}`)}`
        : '',
      data,
      labels: Object.values(periodTableHeadings[period]),
      datasetsLabels: groupedProducts.map(({ name }) => name || ''),
    }));

  return [...groupsToSplit, ...restGroups];
};

export const getFinalSubChapterNames = (subChapterNames: BusinessOperationsKeys[]) => {
  if (subChapterNames.includes('quality')) {
    const newsubChapterNames = [...subChapterNames.slice(0, 6), 'production', 'quality', ...subChapterNames.slice(8)];
    return newsubChapterNames as BusinessOperationsKeys[];
  }
  return subChapterNames;
};

export const getIsBuildingsPremisesDetailsEmpty = (
  buildingsPremisesDetails: IStripedTableRow[],
  isExamplePage: boolean,
) => {
  const areasInfo = buildingsPremisesDetails.find(
    (item) => item.heading === (isExamplePage ? 'Area' : t('pdfPages.mappings.businessOperations.area')),
  )?.info as IInfoObject[];
  const restInfo = buildingsPremisesDetails.filter(
    (item) => item.heading !== (isExamplePage ? 'Area' : t('pdfPages.mappings.businessOperations.area')),
  );

  return areasInfo?.every((item) => !item?.value) && restInfo.every((item) => item.info.every((value) => !value));
};

export const getIsEquipmentGroupsEmpty = (rows: IInfoTableRow[][]) => {
  const filteredRows = rows
    .map((row) =>
      row.filter(({ info }) => (Array.isArray(info) ? info.length : typeof info === 'object' ? info?.imgSrc : info)),
    )
    .filter((array) => array.length);

  if (!filteredRows.length) {
    return true;
  }

  return false;
};
